/**
 * Curated Lucide icons for skill badges — kept small enough for reliable LLM picks.
 * Stored on `Skill.icon` as kebab-case names (e.g. `brain`, `lightbulb`).
 */
export const DEFAULT_SKILL_LUCIDE_ICON = "brain";

export const SKILL_LUCIDE_ICON_ALLOWLIST = [
	"activity",
	"atom",
	"award",
	"book-open",
	"book-marked",
	"brain",
	"briefcase",
	"brush",
	"calculator",
	"chart-bar",
	"chart-line",
	"clipboard-check",
	"clock",
	"code",
	"compass",
	"cpu",
	"ear",
	"eye",
	"file-text",
	"flag",
	"flame",
	"flask-conical",
	"gem",
	"globe",
	"graduation-cap",
	"handshake",
	"heart",
	"heart-pulse",
	"key",
	"languages",
	"layers",
	"lightbulb",
	"link",
	"list-checks",
	"map",
	"megaphone",
	"message-circle",
	"messages-square",
	"microscope",
	"monitor",
	"mountain",
	"network",
	"palette",
	"pen-line",
	"pencil",
	"presentation",
	"puzzle",
	"rocket",
	"route",
	"scale",
	"search",
	"shield",
	"sparkles",
	"speech",
	"star",
	"target",
	"telescope",
	"timer",
	"trending-up",
	"trophy",
	"users",
	"wrench",
	"zap",
] as const;

export type SkillLucideIconName = (typeof SKILL_LUCIDE_ICON_ALLOWLIST)[number];

const SKILL_LUCIDE_ICON_SET = new Set<string>(SKILL_LUCIDE_ICON_ALLOWLIST);

export function isSkillLucideIconName(
	value: string,
): value is SkillLucideIconName {
	return SKILL_LUCIDE_ICON_SET.has(value);
}

/** Normalize LLM / legacy values to an allowlisted Lucide kebab-case name. */
export function normalizeSkillLucideIcon(
	value: string | null | undefined,
): SkillLucideIconName {
	if (value == null) {
		return DEFAULT_SKILL_LUCIDE_ICON;
	}

	const normalized = value
		.trim()
		.toLowerCase()
		.replace(/_/g, "-")
		.replace(/\s+/g, "-");

	if (isSkillLucideIconName(normalized)) {
		return normalized;
	}

	const aliasMap: Record<string, SkillLucideIconName> = {
		thought: "brain",
		thinking: "brain",
		idea: "lightbulb",
		ideas: "lightbulb",
		communication: "messages-square",
		communicate: "messages-square",
		teamwork: "users",
		collaboration: "handshake",
		leadership: "flag",
		creativity: "sparkles",
		creative: "palette",
		analysis: "chart-line",
		analytical: "chart-bar",
		research: "microscope",
		science: "flask-conical",
		technology: "cpu",
		programming: "code",
		writing: "pen-line",
		reading: "book-open",
		learning: "graduation-cap",
		focus: "target",
		resilience: "mountain",
		wellness: "heart-pulse",
		empathy: "heart",
		negotiation: "handshake",
		presentation: "presentation",
		strategy: "compass",
		planning: "route",
		"problem-solving": "puzzle",
	};

	const alias = aliasMap[normalized];
	if (alias) {
		return alias;
	}

	return DEFAULT_SKILL_LUCIDE_ICON;
}

export function skillLucideIconPromptBlock(): string {
	return [
		"Pick one Lucide icon name (kebab-case) that visually represents the skill.",
		`Must be exactly one of: ${SKILL_LUCIDE_ICON_ALLOWLIST.join(", ")}.`,
		"Prefer concrete metaphors (e.g. negotiation → handshake, critical thinking → brain, data analysis → chart-line).",
	].join(" ");
}
